import { TZDate } from "@date-fns/tz";

const LIVE_TRIP_EARLY_START_GRACE_SECONDS = 30 * 60;
const LIVE_TRIP_LATE_FINISH_GRACE_SECONDS = 60 * 60;

function toGtfsStopSequence(value) {
  if (typeof value === "string") {
    if (!/^\d+$/.test(value)) {
      return null;
    }
  } else if (typeof value !== "number") {
    return null;
  }

  const sequence = Number(value);

  return Number.isSafeInteger(sequence) && sequence >= 0 ? sequence : null;
}

export function findTripCall(stops, identity = {}) {
  const { stopId, stopSequence } = identity || {};

  if (!Array.isArray(stops) || !stops.length) {
    return { call: null, index: -1 };
  }

  const sequence = toGtfsStopSequence(stopSequence);
  const index =
    sequence !== null
      ? stops.findIndex(
          (stop) =>
            toGtfsStopSequence(stop.stop_sequence ?? stop.stopSequence) ===
            sequence,
        )
      : stopId
        ? stops.findIndex((stop) => stop.stop_id === stopId)
        : -1;

  return index >= 0 ? { call: stops[index], index } : { call: null, index: -1 };
}

export function formatGtfsStartDate(startDate) {
  if (!/^\d{8}$/.test(String(startDate || ""))) {
    return null;
  }

  return `${startDate.slice(0, 4)}-${startDate.slice(4, 6)}-${startDate.slice(
    6,
    8,
  )}`;
}

export function getScheduledEpochSeconds(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) {
    return null;
  }

  const [year, month, day] = String(dateStr)
    .split("-")
    .map((part) => Number(part));
  const [hour, minute, second = 0] = String(timeStr)
    .split(":")
    .map((part) => Number(part));

  if (
    [year, month, day, hour, minute, second].some(
      (part) => !Number.isFinite(part),
    )
  ) {
    return null;
  }

  const scheduled = new TZDate(year, month - 1, day, 0, 0, 0, 0, timeZone);
  if (hour >= 24) {
    scheduled.setDate(scheduled.getDate() + Math.floor(hour / 24));
  }
  scheduled.setHours(hour % 24, minute, second, 0);

  return Math.round(scheduled.getTime() / 1000);
}

export function normalizeLiveTripTiming({
  vehicleUpdate,
  stopTimesTrip,
  baseDateStr,
  timeZone,
}) {
  const lastScheduledStop =
    Array.isArray(stopTimesTrip) && stopTimesTrip.length > 0
      ? stopTimesTrip[stopTimesTrip.length - 1]
      : null;
  const liveServiceDayStr = formatGtfsStartDate(vehicleUpdate?.trip?.startDate);
  const isLiveServiceDayMatch =
    !baseDateStr || !liveServiceDayStr || baseDateStr === liveServiceDayStr;
  const firstStopTimeUpdate =
    isLiveServiceDayMatch && vehicleUpdate?.update?.stopTimeUpdate?.[0]
      ? vehicleUpdate.update.stopTimeUpdate[0]
      : null;
  const effectiveBaseDateStr = baseDateStr ?? liveServiceDayStr;
  const currentLiveStopId = isLiveServiceDayMatch
    ? vehicleUpdate?.update?.stopId ?? firstStopTimeUpdate?.stopId ?? null
    : null;
  const currentLiveStopSequence = isLiveServiceDayMatch
    ? vehicleUpdate?.update?.currentStopSequence ??
      vehicleUpdate?.update?.stopSequence ??
      firstStopTimeUpdate?.stopSequence
    : null;
  const { call: currentScheduledStop } = findTripCall(stopTimesTrip, {
    stopId: currentLiveStopId,
    stopSequence: currentLiveStopSequence,
  });
  const { call: currentStopTimeUpdate } = findTripCall(
    isLiveServiceDayMatch ? vehicleUpdate?.update?.stopTimeUpdate : null,
    {
      stopId: currentLiveStopId,
      stopSequence: currentLiveStopSequence,
    },
  );

  const currentScheduledDepartureEpoch = getScheduledEpochSeconds(
    effectiveBaseDateStr,
    currentScheduledStop?.departure_time,
    timeZone,
  );
  const scheduledTripStartEpoch = getScheduledEpochSeconds(
    effectiveBaseDateStr,
    stopTimesTrip?.[0]?.departure_time,
    timeZone,
  );
  const scheduledTripEndEpoch = getScheduledEpochSeconds(
    effectiveBaseDateStr,
    lastScheduledStop?.arrival_time ?? lastScheduledStop?.departure_time,
    timeZone,
  );
  const liveDepartureEpoch = Number(currentStopTimeUpdate?.departure?.time);
  const feedDelay = Number(currentStopTimeUpdate?.arrival?.delay);
  const liveEventEpochCandidates = [
    Number(vehicleUpdate?.update?.timestamp),
    Number(vehicleUpdate?.header?.timestamp),
    Number(currentStopTimeUpdate?.departure?.time),
    Number(currentStopTimeUpdate?.arrival?.time),
  ].filter(Number.isFinite);
  const liveEventEpoch = liveEventEpochCandidates[0] ?? null;

  let delaySeconds = 0;
  if (Number.isFinite(feedDelay)) {
    delaySeconds = feedDelay;
  } else if (
    Number.isFinite(currentScheduledDepartureEpoch) &&
      Number.isFinite(liveDepartureEpoch)
  ) {
    delaySeconds = liveDepartureEpoch - currentScheduledDepartureEpoch;
  }

  const isLiveTimingPlausible =
    !isLiveServiceDayMatch ||
    !Number.isFinite(liveEventEpoch) ||
    !Number.isFinite(scheduledTripStartEpoch) ||
    !Number.isFinite(scheduledTripEndEpoch)
      ? isLiveServiceDayMatch
      : liveEventEpoch >=
            scheduledTripStartEpoch - LIVE_TRIP_EARLY_START_GRACE_SECONDS &&
          liveEventEpoch <=
            scheduledTripEndEpoch + LIVE_TRIP_LATE_FINISH_GRACE_SECONDS;

  return {
    currentLiveStopId,
    currentScheduledStop,
    isTerminalStopAssignment: Boolean(
      currentScheduledStop && currentScheduledStop === lastScheduledStop,
    ),
    currentStopTimeUpdate,
    delaySeconds,
    delayMinutes: Math.round(delaySeconds / 60),
    effectiveBaseDateStr,
    isLiveTimingPlausible,
    isLiveServiceDayMatch,
    liveEventEpoch,
    liveServiceDayStr,
    scheduledTripEndEpoch,
    scheduledTripStartEpoch,
  };
}
